import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Trash2, Mail, Phone, ExternalLink, Eye, Inbox, Archive, Settings2, Film, Linkedin, Reply, BookOpen } from "lucide-react";
import { http, formatApiError } from "../lib/api";
import { useRealtime } from "../context/RealtimeContext";
import { useAuth } from "../context/AuthContext";
import { Navigate } from "react-router-dom";
import { XaltixMailSettings } from "../components/XaltixMailSettings";
import { XaltixSignature } from "../components/XaltixSignature";
import { LinkedinVideos } from "../components/LinkedinVideos";
import { XaltixBrochure } from "../components/XaltixBrochure";
import { XaltixGuides } from "../components/XaltixGuides";

const API = process.env.REACT_APP_BACKEND_URL;
const STATUS = {
  new: { label: "Nouveau", cls: "bg-sky-100 text-sky-700 border-sky-200" },
  contacted: { label: "Contacté", cls: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  archived: { label: "Archivé", cls: "bg-slate-100 text-slate-600 border-slate-200" },
};
const TABS = [
  ["active", "En cours", Inbox, "border-sky-600 text-sky-700"],
  ["archived", "Archivés", Archive, "border-slate-600 text-slate-700"],
  ["settings", "Réglages", Settings2, "border-amber-600 text-amber-700"],
  ["videos", "Vidéos LinkedIn", Film, "border-amber-600 text-amber-700"],
  ["guides", "Guides & plaquettes", BookOpen, "border-amber-600 text-amber-700"],
];
const AGENTS_ORDER = ["Moins de 10", "10 à 50", "50 à 200", "Plus de 200"];
const initials = (n) => (n || "?").split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase();
const fmtDT = (iso) => new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });

const SrcBadge = ({ l }) => l.src
  ? <span data-testid={`xaltix-lead-src-${l.id}`} className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-sky-50 text-sky-700 border border-sky-200 font-semibold"><Linkedin className="h-3 w-3" />{l.src.replace("li-", "")}</span>
  : <span className="text-[10px] text-slate-400">Direct</span>;

function LogoManager() {
  const [info, setInfo] = useState(null);
  const ref = useRef();
  const load = () => http.get("/public/xaltix").then((r) => setInfo(r.data));
  useEffect(() => { load(); }, []);
  const upload = async (file) => {
    const fd = new FormData(); fd.append("file", file);
    try { await http.put("/xaltix/logo", fd); toast.success("Logo XALTIX mis à jour"); load(); } catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async () => { await http.delete("/xaltix/logo"); toast.success("Logo retiré"); load(); };
  return (
    <div data-testid="xaltix-logo-manager" className="flex items-center gap-4 bg-slate-900 rounded-md p-4">
      <div className="h-12 min-w-[120px] grid place-items-center">
        {info?.has_logo ? <img data-testid="xaltix-logo-preview" src={`${API}/api/public/xaltix/logo?v=${encodeURIComponent(info.logo_v)}`} alt="Logo XALTIX" className="h-12 w-auto" />
          : <span className="text-xl font-extrabold tracking-[0.18em] text-white">XALT<span className="text-amber-400">I</span>X</span>}
      </div>
      <input ref={ref} data-testid="xaltix-logo-upload-input" type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => e.target.files[0] && upload(e.target.files[0])} />
      <button data-testid="xaltix-logo-upload-btn" onClick={() => ref.current.click()} className="inline-flex items-center gap-1.5 bg-amber-400 text-slate-950 px-3 py-2 rounded-md text-sm font-bold"><Upload className="h-4 w-4" /> {info?.has_logo ? "Remplacer le logo" : "Insérer le logo"}</button>
      {info?.has_logo && <button data-testid="xaltix-logo-remove-btn" onClick={remove} className="inline-flex items-center gap-1.5 text-rose-300 text-sm font-semibold"><Trash2 className="h-4 w-4" /> Retirer</button>}
    </div>
  );
}

function LeadDialog({ l, onClose, onChange }) {
  const [note, setNote] = useState(l.note || "");
  const patch = async (body, msg) => {
    try { onChange((await http.patch(`/xaltix/leads/${l.id}`, body)).data); if (msg) toast.success(msg); } catch (e) { toast.error(formatApiError(e)); }
  };
  const Row = ({ k, v }) => <div className="flex gap-3"><span className="w-32 text-slate-500 shrink-0">{k}</span><span className="font-medium break-all">{v || "—"}</span></div>;
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 grid place-items-center p-4">
      <div data-testid="xaltix-lead-dialog" className="bg-white rounded-md w-full max-w-2xl border border-slate-200 max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-display text-lg font-bold">{l.name} <span className="text-slate-500 font-medium">· {l.company}</span>{l.lang === "en" && <span data-testid={`xaltix-lead-detail-lang-${l.id}`} className="ml-1.5 inline-block text-[10px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-800 border border-amber-200 font-bold align-middle">EN</span>}</h3>
          <button data-testid="xaltix-lead-dialog-close" onClick={onClose} className="p-1 hover:bg-slate-100 rounded text-slate-500">✕</button>
        </div>
        <div className="p-5 space-y-4 text-sm">
          <div className="flex flex-wrap items-center gap-2">
            <span className={`text-[10px] px-2 py-0.5 rounded border ${STATUS[l.status].cls}`}>{STATUS[l.status].label}</span>
            <SrcBadge l={l} />
            {l.ack_result && <span className={`text-[11px] font-semibold ${l.ack_result.sent ? "text-emerald-600" : "text-amber-600"}`} title={l.ack_result.reason || ""}>{l.ack_result.sent ? "Remerciement envoyé" : "Remerciement non envoyé"}</span>}
            <span className="ml-auto text-xs text-slate-500">Reçu le {fmtDT(l.created_at)}</span>
          </div>
          <div className="space-y-1.5">
            <Row k="Email" v={<a href={`mailto:${l.email}`} className="text-sky-700">{l.email}</a>} />
            <Row k="Téléphone" v={l.phone && <a href={`tel:${l.phone}`} className="text-sky-700">{l.phone}</a>} />
            <Row k="Société" v={l.company} />
            <Row k="Nombre d'agents" v={l.agents} />
          </div>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Message</div>
            <div data-testid="xaltix-lead-message" className="bg-slate-50 border border-slate-200 rounded-md p-3 whitespace-pre-line">{l.message || "—"}</div>
          </div>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Note interne</div>
            <textarea data-testid={`xaltix-lead-note-${l.id}`} rows={3} value={note} onChange={(e) => setNote(e.target.value)} className="w-full border border-slate-300 rounded-md px-2.5 py-1.5 text-sm" placeholder="Suivi, rendez-vous, besoins…" />
          </div>
          <div className="flex flex-wrap gap-2 pt-1">
            <a data-testid="xaltix-lead-reply" href={`mailto:${l.email}?subject=${encodeURIComponent("XALTIX — votre demande")}`} className="inline-flex items-center gap-1.5 bg-slate-900 text-white px-3 py-1.5 rounded-md text-xs font-semibold"><Reply className="h-3.5 w-3.5" /> Répondre</a>
            <button data-testid="xaltix-lead-save-note" onClick={() => patch({ note }, "Note enregistrée")} className="text-xs font-semibold px-3 py-1.5 rounded-md border border-slate-300 hover:bg-slate-50">Enregistrer la note</button>
            {Object.keys(STATUS).filter((s) => s !== l.status).map((s) => (
              <button key={s} data-testid={`xaltix-lead-${s}-${l.id}`} onClick={() => patch({ status: s, note }, `Statut : ${STATUS[s].label}`)} className="text-xs font-semibold px-3 py-1.5 rounded-md border border-slate-300 hover:bg-slate-50">
                {s === "new" ? "Remettre en nouveau" : s === "contacted" ? "Marquer contacté" : "Archiver"}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function LeadsTable({ rows, onOpen, empty }) {
  return (
    <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-widest text-slate-500">
          <tr><th className="py-2 px-3 text-left">Contact</th><th className="py-2 px-3 text-left">Coordonnées</th><th className="py-2 px-3 text-left">Société</th>
            <th className="py-2 px-3 text-left">Agents</th><th className="py-2 px-3 text-left">Origine</th><th className="py-2 px-3 text-left">Statut</th><th className="py-2 px-3 text-right">Actions</th></tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rows.length === 0 ? <tr><td colSpan={7} data-testid="xaltix-leads-empty" className="py-6 text-center text-slate-500 text-sm">{empty}</td></tr> : rows.map((l) => (
            <tr key={l.id} data-testid={`xaltix-lead-${l.id}`} className="hover:bg-slate-50 cursor-pointer" onClick={() => onOpen(l)}>
              <td className="py-2 px-3">
                <div className="flex items-center gap-2.5">
                  <div className="h-10 w-10 rounded bg-slate-900 text-amber-300 grid place-items-center text-xs font-bold shrink-0">{initials(l.name)}</div>
                  <div>
                    <div className="font-medium">{l.name}</div>
                    <div className="text-[11px] text-slate-500">{fmtDT(l.created_at)}</div>
                  </div>
                </div>
              </td>
              <td className="py-2 px-3 text-xs">
                <div className="text-sky-700 inline-flex items-center gap-1"><Mail className="h-3 w-3" />{l.email}</div>
                {l.phone && <div className="text-slate-600 inline-flex items-center gap-1"><Phone className="h-3 w-3" />{l.phone}</div>}
              </td>
              <td className="py-2 px-3 text-xs font-semibold">{l.company}{l.lang === "en" && <span data-testid={`xaltix-lead-lang-${l.id}`} className="ml-1.5 inline-block text-[10px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-800 border border-amber-200 font-bold align-middle">EN</span>}{l.message && <div className="text-[11px] font-normal text-slate-500 truncate max-w-[220px]">{l.message}</div>}</td>
              <td className="py-2 px-3 text-xs">{l.agents || "—"}</td>
              <td className="py-2 px-3"><SrcBadge l={l} /></td>
              <td className="py-2 px-3"><span className={`text-[10px] px-2 py-0.5 rounded border ${STATUS[l.status].cls}`}>{STATUS[l.status].label}</span></td>
              <td className="py-2 px-3 text-right"><button data-testid={`xaltix-lead-view-${l.id}`} onClick={(e) => { e.stopPropagation(); onOpen(l); }} className="p-1.5 rounded hover:bg-slate-100"><Eye className="h-4 w-4 text-slate-600" /></button></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function XaltixLeads() {
  const { user } = useAuth();
  return user?.is_superadmin || user?.staff_perms?.includes("leads") ? <LeadsPage /> : <Navigate to="/" replace />;
}

function LeadsPage() {
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState("active");
  const [filter, setFilter] = useState("all");
  const [sort, setSort] = useState("recent");
  const [selected, setSelected] = useState(null);
  const { lastEvent } = useRealtime();
  const load = () => http.get("/xaltix/leads").then((r) => setItems(r.data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);
  useEffect(() => { if (lastEvent?.event === "xaltix_lead") load(); }, [lastEvent]); // eslint-disable-line react-hooks/exhaustive-deps
  const tabOf = (l) => (l.status === "archived" ? "archived" : "active");
  const inTab = items.filter((l) => tabOf(l) === tab);
  const filtered = tab !== "active" || filter === "all" ? inTab : inTab.filter((l) => l.status === filter);
  const shown = [...filtered].sort((a, b) => {
    if (sort === "company") return (a.company || "").localeCompare(b.company || "", "fr", { sensitivity: "base" });
    if (sort === "name") return (a.name || "").localeCompare(b.name || "", "fr", { sensitivity: "base" });
    if (sort === "agents") return AGENTS_ORDER.indexOf(b.agents) - AGENTS_ORDER.indexOf(a.agents);
    return (b.created_at || "").localeCompare(a.created_at || "");
  });
  const onChange = (n) => { setItems((cur) => cur.map((x) => (x.id === n.id ? n : x))); setSelected(n); };
  return (
    <div data-testid="xaltix-leads-page" className="space-y-4 p-8 max-w-[1800px] mx-auto">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-2xl font-extrabold">Contacts XALTIX</h1>
          <p className="text-sm text-slate-600">{items.filter((l) => l.status !== "archived").length} demande(s) en cours, reçues via le formulaire de la page XALTIX.</p>
        </div>
        <a data-testid="xaltix-open-page" href="/xaltix" target="_blank" rel="noreferrer" className="text-xs text-slate-500 bg-slate-100 rounded-md px-3 py-2 font-mono inline-flex items-center gap-1.5 hover:text-slate-800">
          <ExternalLink className="h-3.5 w-3.5" /> solutions-prime.fr/xaltix
        </a>
      </div>

      <div className="flex gap-1 border-b border-slate-200 flex-wrap" data-testid="xaltix-tabs">
        {TABS.map(([k, l, Icon, cls]) => (
          <button key={k} data-testid={`xaltix-tab-${k}`} onClick={() => { setTab(k); setFilter("all"); }}
            className={`px-4 py-2 text-sm font-semibold -mb-px border-b-2 inline-flex items-center gap-1.5 transition-colors ${tab === k ? cls : "border-transparent text-slate-500 hover:text-slate-800"}`}>
            <Icon className="h-3.5 w-3.5" />{l}
            {(k === "active" || k === "archived") && <span className="text-[11px] rounded-full bg-slate-100 px-1.5 py-0.5 text-slate-600">{items.filter((i) => tabOf(i) === k).length}</span>}
          </button>
        ))}
      </div>

      {tab === "settings" && <div className="space-y-4"><XaltixSignature /><LogoManager /><XaltixMailSettings /></div>}
      {tab === "guides" && <XaltixGuides />}
      {tab === "videos" && <div className="space-y-4"><XaltixBrochure /><LinkedinVideos /></div>}
      {(tab === "active" || tab === "archived") && <>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex gap-1 flex-wrap">
            {tab === "active" && ["all", "new", "contacted"].map((s) => (
              <button key={s} data-testid={`xaltix-filter-${s}`} onClick={() => setFilter(s)}
                className={`text-xs font-semibold px-3 py-1.5 rounded border ${filter === s ? "text-white" : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"}`}
                style={filter === s ? { backgroundColor: "var(--brand-primary)", borderColor: "var(--brand-primary)" } : {}}>
                {s === "all" ? `Tous (${inTab.length})` : `${STATUS[s].label} (${inTab.filter((i) => i.status === s).length})`}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2 text-xs">
            <label className="text-slate-500 font-medium">Trier par :</label>
            <select data-testid="xaltix-sort" value={sort} onChange={(e) => setSort(e.target.value)} className="border border-slate-300 rounded px-2 py-1.5 bg-white text-slate-700 text-xs">
              <option value="recent">Date (plus récent)</option>
              <option value="company">Société (A→Z)</option>
              <option value="name">Nom (A→Z)</option>
              <option value="agents">Nombre d'agents (décroissant)</option>
            </select>
          </div>
        </div>
        <LeadsTable rows={shown} onOpen={setSelected} empty={tab === "active" ? "Aucune demande de contact pour le moment." : "Aucun contact archivé."} />
      </>}

      {selected && <LeadDialog l={selected} onClose={() => setSelected(null)} onChange={onChange} />}
    </div>
  );
}
